/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { useContext } from 'react';

import type { Faq } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import type { EnterpriseArticleFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
import EnterpriseArticleBody from 'bundles/ent-website/articles/components/EnterpriseArticleBody';
import EnterpriseArticleSources from 'bundles/ent-website/articles/components/EnterpriseArticleSources';
import EnterpriseRelatedArticles from 'bundles/ent-website/articles/components/EnterpriseRelatedArticles';
import { genBreadcrumbsForDomain } from 'bundles/ent-website/articles/utils/entBreadcrumbUtils';
import { createCanonicalEntArticleLink } from 'bundles/ent-website/articles/utils/entSeoUtils';
import { EntWebsiteDomainContext } from 'bundles/ent-website/components/EntWebsiteDomainContext';
import { CourseraMetatags } from 'bundles/seo';
import ArticleFooter from 'bundles/shared-articles/components/ArticleFooter';
import ArticleHeader from 'bundles/shared-articles/components/ArticleHeader';
import ArticleGoogleSchemaMarkUp from 'bundles/shared-articles/components/seo/ArticleGoogleSchemaMarkUp';
import ArticlePageFaqGoogleSchemaMarkup from 'bundles/shared-articles/components/seo/ArticlePageFaqGoogleSchemaMarkup';
import { DEFAULT_ARTICLE_AUTHOR } from 'bundles/shared-articles/constants';

const styles = {
  articlePage: css`
    background-color: white;
    overflow-x: hidden;
    overflow-y: auto;
    position: relative;
    width: 100%;
  `,
  articleContainer: css`
    max-width: 100%;
    margin: 0 auto;

    img {
      max-width: 100% !important;
    }
  `,
  contentContainer: css`
    margin-bottom: 4rem;
  `,
};

export type EnterpriseArticlePageProps = {
  pageData?: EnterpriseArticleFragment | null;
  slug?: string | null;
};

const EnterpriseArticlePage = ({ pageData, slug }: EnterpriseArticlePageProps) => {
  const { domain } = useContext(EntWebsiteDomainContext);

  if (!pageData) {
    return null;
  }

  const {
    title,
    intro,
    featuredImage,
    featuredImageLink,
    body,
    sources,
    relatedArticlesCollection,
    metaTitle,
    metaDescription,
    sys,
  } = pageData;

  if (!title || !slug || !intro || !body || !body?.json) {
    return null;
  }

  const featuredImageUrl = featuredImage?.url;
  const firstPublishedAt = sys?.firstPublishedAt;
  const publishedAt = sys?.publishedAt;

  const currDate = new Date();

  const byLineData = {
    authorNames: DEFAULT_ARTICLE_AUTHOR.name,
    // If this is undefined, someone is seeing this article in preview.
    // In that case, set current date as default.
    date: publishedAt || currDate.toISOString(),
    title,
  };

  const articleInlineImages = body?.links?.assets?.block;
  const articleInlineImagesUrls = articleInlineImages.length > 0 ? articleInlineImages.map((image) => image?.url) : [];
  const imageUrls = [featuredImageUrl, ...articleInlineImagesUrls].filter((url) => !!url);

  const faqs = body?.links?.entries?.block
    .filter((linkEntry) => linkEntry?.__typename === 'Faq')
    .map((faqEntry) => ({
      question: (faqEntry as Faq).question,
      answer: (faqEntry as Faq).answer,
    }));
  const bodyJson = body.json;
  const links = body?.links;
  const { authorNames, date } = byLineData;

  return (
    <div className="rc-ArticlePage cds" data-e2e="article-page">
      <div css={styles.articlePage}>
        <div css={styles.articleContainer} style={{ minHeight: '100vh' }}>
          <ArticleHeader
            title={title}
            featuredImage={featuredImage}
            featuredImageLink={featuredImageLink}
            subtitle={intro}
            authorNames={authorNames}
            latestPublishedDate={date}
            breadcrumbLinks={[...genBreadcrumbsForDomain(domain)]}
          />
          <div css={styles.contentContainer}>
            <EnterpriseArticleBody body={bodyJson} links={links} module={pageData} />
          </div>
          <EnterpriseArticleSources body={sources?.json} module={pageData} />
          <EnterpriseRelatedArticles articles={relatedArticlesCollection?.items ?? []} />
          {!!firstPublishedAt && !!title && <ArticleFooter authorNames={authorNames} date={date} title={title} />}
        </div>
        <CourseraMetatags
          metaNameAndProperties={{
            title: metaTitle || title,
            ogAndTwitterTitle: metaTitle || title,
            description: metaDescription || intro,
            absoluteImageHref: featuredImageUrl || undefined,
            disableCourseraDescriptionSuffix: true,
          }}
        />
        <ArticlePageFaqGoogleSchemaMarkup faqs={faqs} />
        <ArticleGoogleSchemaMarkUp
          title={title}
          imageUrls={imageUrls}
          createdAt={firstPublishedAt}
          updatedAt={publishedAt}
          breadcrumbs={[]}
          id={`${createCanonicalEntArticleLink(slug)}#article`}
        />
      </div>
    </div>
  );
};

export default EnterpriseArticlePage;
